import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Grid,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon
} from '@mui/icons-material';
import planService from '../../services/planService';
import AddEditPlan from './AddEditPlan';
import ViewPlan from './ViewPlan';

const PlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  
  // Dialog states
  const [openAddEdit, setOpenAddEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, planId: null, planName: '' });

  useEffect(() => {
    fetchPlans();
  }, [showActiveOnly]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await planService.getAllPlans(showActiveOnly);
      setPlans(response.data || []);
      setError('');
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlan = () => {
    setSelectedPlan(null);
    setEditMode(false);
    setOpenAddEdit(true);
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setEditMode(true);
    setOpenAddEdit(true);
  };

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
    setOpenView(true);
  };

  const handleDeletePlan = (plan) => {
    setDeleteConfirm({
      open: true,
      planId: plan._id,
      planName: plan.name
    });
  };

  const confirmDelete = async () => {
    try {
      await planService.deletePlan(deleteConfirm.planId);
      setSuccess('Plan deleted successfully');
      setDeleteConfirm({ open: false, planId: null, planName: '' });
      fetchPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      setError('Failed to delete plan');
    }
  };

  const handleToggleStatus = async (plan) => {
    try {
      await planService.togglePlanStatus(plan._id);
      setSuccess(`Plan ${plan.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchPlans();
    } catch (error) {
      console.error('Error toggling plan status:', error);
      setError('Failed to toggle plan status');
    }
  };

  const handlePlanSaved = () => {
    setOpenAddEdit(false);
    setSuccess(editMode ? 'Plan updated successfully' : 'Plan created successfully');
    fetchPlans();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  const formatDuration = (days) => {
    if (days < 30) {
      return `${days} days`;
    } else if (days === 30) {
      return '1 month';
    } else if (days < 365) {
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      return remainingDays > 0 ? `${months} months ${remainingDays} days` : `${months} months`;
    } else {
      const years = Math.floor(days / 365);
      const remainingDays = days % 365;
      return remainingDays > 0 ? `${years} years ${remainingDays} days` : `${years} years`;
    }
  };

  const getPlanAccessSummary = (plan) => {
    const permissions = Array.isArray(plan.permissions) ? plan.permissions : [];
    const contactLimit = plan.contact_view_limit ?? permissions.find((permission) => permission.slug === 'view_contact_details')?.limit ?? 0;
    const officeProfiles = plan.office_assisted_profiles ?? permissions.find((permission) => permission.slug === 'office_assisted_profiles')?.limit ?? 0;
    const hasChat = plan.unlimited_chat ?? permissions.some((permission) => permission.slug === 'chat_messages' && permission.is_allowed);

    return { contactLimit, officeProfiles, hasChat };
  };

  const popularPlans = [...plans]
    .filter((plan) => (plan.enrolledUsers || 0) > 0)
    .sort((a, b) => (b.enrolledUsers || 0) - (a.enrolledUsers || 0))
    .slice(0, 3);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading plans...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Grid item>
          <Typography variant="h4" gutterBottom>
            Plans Management
          </Typography>
        </Grid>
        <Grid item>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showActiveOnly}
                  onChange={(e) => setShowActiveOnly(e.target.checked)}
                />
              }
              label="Active Only"
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPlan}
            >
              Add Plan
            </Button>
          </Box>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {popularPlans.length > 0 ? (
          popularPlans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={plan._id}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Popular Plan #{index + 1}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {plan.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatPrice(plan.price)} • {formatDuration(plan.duration_days)}
                  </Typography>
                  <Chip
                    label={`${plan.enrolledUsers || 0} users enrolled`}
                    size="small"
                    color="primary"
                    sx={{ mt: 1.5 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography color="text.secondary">No enrolled users found for plans yet.</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Enrolled Users</TableCell>
                  <TableCell>Plan Access</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No plans found
                    </TableCell>
                  </TableRow>
                ) : (
                  plans.map((plan) => (
                    <TableRow key={plan._id}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {plan.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatPrice(plan.price)}</TableCell>
                      <TableCell>{formatDuration(plan.duration_days)}</TableCell>
                      <TableCell>
                        <Chip
                          label={plan.enrolledUsers || 0}
                          size="small"
                          color={(plan.enrolledUsers || 0) > 0 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const access = getPlanAccessSummary(plan);
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              <Chip label={`${access.contactLimit} contacts`} size="small" variant="outlined" />
                              {access.hasChat && <Chip label="Unlimited chat" size="small" color="success" variant="outlined" />}
                              {access.officeProfiles > 0 && <Chip label={`${access.officeProfiles} office profiles`} size="small" color="warning" variant="outlined" />}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plan.is_active ? 'Active' : 'Inactive'}
                          color={plan.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(plan.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewPlan(plan)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditPlan(plan)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={plan.is_active ? 'Deactivate' : 'Activate'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleStatus(plan)}
                            >
                              {plan.is_active ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeletePlan(plan)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Plan Dialog */}
      <Dialog
        open={openAddEdit}
        onClose={() => setOpenAddEdit(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Plan' : 'Add New Plan'}
        </DialogTitle>
        <DialogContent>
          <AddEditPlan
            plan={selectedPlan}
            editMode={editMode}
            onSave={handlePlanSaved}
            onCancel={() => setOpenAddEdit(false)}
          />
        </DialogContent>
      </Dialog>

      {/* View Plan Dialog */}
      <Dialog
        open={openView}
        onClose={() => setOpenView(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Plan Details</DialogTitle>
        <DialogContent>
          <ViewPlan plan={selectedPlan} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, planId: null, planName: '' })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the plan "{deleteConfirm.planName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirm({ open: false, planId: null, planName: '' })}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlansManagement;
